import { DIALER_NUMBER_LIMIT } from '@modules/phone/constants/dialer.constants';
import { COUNTRY_FLAG_BY_COUNTRY_CODE } from '@modules/settings/my-numbers/purchase-number/constants/purchase-number.constant';
import { useModal } from '@shared/hooks/use-modal';
import { replaceDialingCode } from '@shared/utils/country.helper';
import { ChangeEvent } from 'react';
import { BasicPhoneInputProps } from './phone-input.types';

export const useBasicPhoneInput = ({
  value,
  onChange,
  allowSpecialCharacters,
  bordered,
  countryCode,
  size = 'large',
}: BasicPhoneInputProps) => {
  const { open, handleClose, handleChange: handleOpenChange } = useModal();

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    // Clean input to handle multiple "+" signs
    let cleanedInput = event.target.value.trim();
    cleanedInput = cleanedInput.replace(allowSpecialCharacters ? /[^0-9+*#]/g : /[^0-9+]/g, '');

    // If there are multiple "+", keep the part after the last "+"
    const plusIndex = cleanedInput.indexOf('+', 1); // Find the second "+" if it exists
    if (plusIndex !== -1) {
      cleanedInput = cleanedInput.slice(plusIndex);
    }

    if (cleanedInput.length <= DIALER_NUMBER_LIMIT) onChange(cleanedInput);
  };

  const handleCountrySelect = (dialingCode: string, newCountryCode: string) => {
    onChange(replaceDialingCode(value, dialingCode), newCountryCode);
  };

  const selectedCountryFlag = COUNTRY_FLAG_BY_COUNTRY_CODE[countryCode?.toUpperCase() ?? ''];

  const getFlagWrapperClass = () => {
    if (bordered) return 'flex gap-1 my-auto cursor-pointer w-[51px]';
    if (size === 'small') return 'size-5 -mt-0.5';
    return 'size-[27px] relative mt-0.5';
  };

  const getFlagClass = () => {
    const isNP = countryCode?.toLowerCase() === 'np';

    if (size === 'large') {
      if (isNP) return bordered ? 'size-5' : 'size-5 mt-0.5';
      if (bordered) return 'w-full h-5';
      return 'size-full rounded-full';
    } else {
      if (isNP) return 'size-4 -mt-1';
      return 'size-5 rounded-full -mt-1';
    }
  };

  const getInputClass = () => {
    let className = 'border-none! outline-none! bg-transparent! shadow-none! tracking-wide';
    if (bordered) className += ' h-[38px] flex-1 px-2.75 py-[8.2px] text-[15px]!';
    else if (size === 'large') className += ' text-xl!';

    if (size === 'small') className += ' text-sm!';
    return className;
  };

  return {
    open,
    handleClose,
    handleOpenChange,
    handleChange,
    handleCountrySelect,
    selectedCountryFlag,
    getFlagWrapperClass,
    getFlagClass,
    getInputClass,
  };
};
